/**
 * Hooks for interacting with the right sidebar state.
 *
 * Generally, we try to abstract the state management layer in here, the callers should be able to think about
 * the data they want, vs managing internals like query params.
 */

import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { fullNavigationState } from "@/lib/navigationState.ts";
import { useCallback, useMemo, useRef } from "react";
import { useLocation, useSearch } from "wouter";
import { z } from "zod";
import { useShellUrlWriter } from "@/components/navigation/shellUrl.ts";
import { getMobilePageRoute } from "./routing.ts";
import { sidebarSchema } from "./sidebarSchema.ts";

const sidebarQueryParamPrefix = "sb_";
const showBackParam = `${sidebarQueryParamPrefix}_showBack`;

/**
 * Sidebar-owned query-state writes use `fullNavigationState` so navigation
 * boundaries do not present their canonical page URLs in-place. For example,
 * closing a sidebar while standing on `/contacts/1` is indistinguishable from
 * clicking a real link to that page without this explicit intent.
 */
/**
 * Reserved param holding the "back" target: the encoded `sb_*` query string of
 * the sidebar that was open when this one was opened with `{ withBack: true }`.
 * Going back decodes it and re-opens that parent. Because the captured blob
 * itself includes any nested `sb__back`, back chains multiple levels deep.
 */
const sidebarBackParam = `${sidebarQueryParamPrefix}_back`;

export type SidebarOpenOptions = {
  /** Show a back arrow button on the left of the sidebar header. */
  showBack?: boolean;
  /**
   * Capture the currently-open sidebar as a "back" target so the newly opened
   * sidebar shows a Back button that returns to it. The parent is captured
   * dynamically from the live URL, so any sidebar can open any other and still
   * offer a correct "back" — no caller needs to know who its parent is.
   */
  withBack?: boolean;
  /**
   * Whether opening the sidebar should push a new browser-history entry.
   * When true, the browser back button closes the sidebar. When false, back
   * navigates away from the current page.
   *
   * Default (when omitted):
   * - Mobile first-open pushes (back closes the full-screen sheet).
   * - Desktop first-open replaces (back leaves the page; no history clutter).
   *
   * Override this when desktop UX should differ — e.g. on Home where opening
   * the calendar day view is a "sub-view" users expect to back out of.
   */
  pushHistory?: boolean;
};

/**
 * Fields in the sidebar schema that are arrays of strings. A single query-param
 * appearance should still yield a one-element array (rather than a bare string)
 * so Zod's `z.array(z.string())` validator accepts it. Repeats naturally collect
 * via `URLSearchParams.getAll()`.
 *
 * Empty today — no sidebar type carries an array field since the calendar day
 * view (whose `preselectedRanges` was the only one) was retired. Kept so adding
 * one back is a one-line change rather than a re-derivation of why a repeated
 * param arrived as a bare string.
 */
const ARRAY_SIDEBAR_FIELDS = new Set<string>();

/**
 * Sidebar types that have been retired but still appear in old URLs — bookmarks,
 * shared links, history entries. Recognized so a stale link is quietly ignored
 * rather than logged as a malformed sidebar state, which would bury the real
 * parse failures this error is for.
 */
const RETIRED_SIDEBAR_TYPES = new Set(["eventEdit"]);

/**
 * Given a URLSearchParams object, return the sidebar state. Repeated params
 * for array-valued fields are collected via `getAll()`; single-valued fields
 * read via `get()`.
 */
const getSidebarStateFromQueryParams = (queryParams: URLSearchParams) => {
  // If there is no sidebar type param, the sidebar is not open, don't try to zod parse, bail immediately
  if (!queryParams.has(`${sidebarQueryParamPrefix}type`)) {
    return null;
  }

  // A retired type is a known-stale URL, not a malformed one.
  if (RETIRED_SIDEBAR_TYPES.has(queryParams.get(`${sidebarQueryParamPrefix}type`) ?? "")) {
    return null;
  }

  const accumulated: Record<string, string | string[]> = {};
  const seen = new Set<string>();
  for (const rawKey of queryParams.keys()) {
    if (!rawKey.startsWith(sidebarQueryParamPrefix)) continue;
    if (seen.has(rawKey)) continue;
    seen.add(rawKey);
    const key = rawKey.slice(sidebarQueryParamPrefix.length);
    accumulated[key] = ARRAY_SIDEBAR_FIELDS.has(key)
      ? queryParams.getAll(rawKey)
      : (queryParams.get(rawKey) ?? "");
  }

  const parsed = sidebarSchema.safeParse(accumulated);
  if (!parsed.success) {
    console.error("Invalid sidebar state", parsed.error);
    return null;
  }

  return parsed.data;
};

/**
 * Hook to get the current sidebar state based on the URL query params.
 *
 * Primarily intended to be used by the RightSidebar component itself.
 */
export const useRightSidebarQueryParamState = () => {
  // Get the sidebar state based on the URL query params
  const urlQueryParams = useSearch();
  const queryParamSidebarState = useMemo(() => {
    const queryParams = new URLSearchParams(urlQueryParams);
    return getSidebarStateFromQueryParams(queryParams);
  }, [urlQueryParams]);

  return queryParamSidebarState;
};

/**
 * The event id in a legacy `?sb_type=eventEdit&sb_eventId=…` URL, or undefined.
 *
 * That sidebar is gone, so the schema no longer parses these — but the URLs
 * outlive it in bookmarks, shared links, and history. Read straight off the
 * params so a surface that *can* present an event takes them over.
 */
export const useLegacyEventSidebarId = (): string | undefined => {
  const urlQueryParams = useSearch();
  return useMemo(() => {
    const queryParams = new URLSearchParams(urlQueryParams);
    if (queryParams.get(`${sidebarQueryParamPrefix}type`) !== "eventEdit") return undefined;
    return queryParams.get(`${sidebarQueryParamPrefix}eventId`) ?? undefined;
  }, [urlQueryParams]);
};

/**
 * Whether the current sidebar should show a back button.
 */
export const useSidebarShowBack = (): boolean => {
  const urlQueryParams = useSearch();
  return useMemo(() => {
    const queryParams = new URLSearchParams(urlQueryParams);
    return queryParams.get(showBackParam) === "true";
  }, [urlQueryParams]);
};

/**
 * Generic "back to the parent sidebar" navigation. When the current sidebar was
 * opened with `{ withBack: true }`, `canGoBack` is true and `goBack()` restores
 * the captured parent sidebar (and its own back target, so chains unwind one
 * level per call). Any sidebar header can wire this up — it's not specific to
 * any sidebar type.
 */
export const useSidebarBack = (): { canGoBack: boolean; goBack: () => void } => {
  const urlQueryParams = useSearch();
  const writeShellUrl = useShellUrlWriter();

  const canGoBack = useMemo(
    () => !!new URLSearchParams(urlQueryParams).get(sidebarBackParam),
    [urlQueryParams],
  );

  const goBack = useCallback(() => {
    if (!canGoBack) return;
    writeShellUrl(
      (current) => {
        const back = new URLSearchParams(current.search).get(sidebarBackParam);
        // Drop the current sidebar's params, then restore the parent's — as
        // repeated params where the parent had them, since the blob was built
        // from `append`.
        const params: Record<string, string[]> = {};
        for (const [key, value] of new URLSearchParams(back ?? "").entries()) {
          params[key] = [...(params[key] ?? []), value];
        }
        return { dropPrefix: sidebarQueryParamPrefix, params };
      },
      { replace: true, state: fullNavigationState },
    );
  }, [canGoBack, writeShellUrl]);

  return { canGoBack, goBack };
};

/**
 * Hook to open and close the right sidebar.
 *
 * The sidebar is opened by setting the query params, and closed by removing the query params.
 * The component listens to the URL change and will respond.
 */
export const useRightSidebar = () => {
  // `setLocation` is for leaving the page — a sidebar that graduates to its
  // own route on mobile. Writes to the page's query go through the URL writer.
  const [, setLocation] = useLocation();
  const writeShellUrl = useShellUrlWriter();
  const isMobile = useIsMobile();
  const queryParamSidebarState = useRightSidebarQueryParamState();
  const isOpen = !!queryParamSidebarState;
  // Guard against double-dismiss on mobile: history.back() is async, so a
  // second closeSidebar call before popstate fires would pop an extra entry.
  const closePendingRef = useRef(false);

  /** To open the sidebar, set its query params. The component listens to the URL change and will respond. */
  const openSidebar = useCallback(
    (sidebarContent: z.infer<typeof sidebarSchema>, options?: SidebarOpenOptions) => {
      // On mobile, sidebar types with a registered `mobilePageRoute` graduate
      // to a full-screen page instead of rendering the Sheet overlay. Skip the
      // `sb_*` write entirely and navigate directly — keeps history clean (no
      // intermediate sb_* entry to back-through) and lets the page own the UX.
      if (isMobile) {
        const mobileRoute = getMobilePageRoute(sidebarContent);
        if (mobileRoute) {
          setLocation(mobileRoute);
          return;
        }
      }

      // The sidebar's own params, prefixed (the consumer strips the prefix on
      // read). Undefined/null optional schema fields are skipped — `.toString()`
      // on undefined throws, and Zod would fail to parse the string back into
      // the optional type anyway. Arrays go out as repeated params
      // (`?sb_foo=a&sb_foo=b`) so they round-trip through the reader's
      // `getAll()`, matching the public URL convention for repeatable fields.
      const params: Record<string, string | string[]> = {};
      for (const [key, value] of Object.entries(sidebarContent)) {
        if (value === undefined || value === null) continue;
        const paramKey = `${sidebarQueryParamPrefix}${key}`;
        params[paramKey] = Array.isArray(value)
          ? value.filter((item) => item !== undefined && item !== null).map(String)
          : value.toString();
      }
      if (options?.showBack) params[showBackParam] = "true";

      // Desktop: the sidebar is overlay state — never a history entry, so always
      // replace (no Back step, even when swapping content). Mobile: push on
      // first open so the Sheet's hardware/OS Back closes it; replace when
      // swapping. `options.pushHistory` is ignored — desktop is never pushed.
      const isSwapping = !!queryParamSidebarState;
      const push = isMobile && !isSwapping;

      // A function patch, because the back target is whatever sidebar is open
      // at the moment of the write — read off the location the write applies
      // to, not off this render's copy, so a sidebar opened in the same tick
      // as another writer's change still captures the right parent.
      writeShellUrl(
        (current) => {
          // Capture the currently-open sidebar (if any) as the back target:
          // every `sb_*` key (so a nested `sb__back` carries through and back
          // chains), stashed as one encoded value.
          if (options?.withBack) {
            const parent = new URLSearchParams();
            for (const [key, value] of new URLSearchParams(current.search).entries()) {
              if (key.startsWith(sidebarQueryParamPrefix)) parent.append(key, value);
            }
            if (parent.has(`${sidebarQueryParamPrefix}type`)) {
              params[sidebarBackParam] = parent.toString();
            }
          }
          return { dropPrefix: sidebarQueryParamPrefix, params };
        },
        { replace: !push, state: fullNavigationState },
      );
    },
    [setLocation, writeShellUrl, isMobile, queryParamSidebarState],
  );

  /** To close the sidebar, remove all sidebar related query params. */
  const closeSidebar = useCallback(() => {
    // On mobile the sidebar was opened with a history push, so pop it.
    // Fallback to query-param removal for deep links where there's no push to
    // pop (and on desktop, where the sidebar is never pushed).
    if (isMobile && (history.state?._appDepth ?? 0) > 0) {
      if (closePendingRef.current) return;
      closePendingRef.current = true;
      const onPop = () => {
        closePendingRef.current = false;
        window.removeEventListener("popstate", onPop);
      };
      window.addEventListener("popstate", onPop);
      history.back();
      return;
    }

    // Every sidebar param and nothing else. The writer keeps whatever the rest
    // of the page has in the query — which is exactly the clobbering this used
    // to be capable of, rebuilding the whole query from this render's copy.
    writeShellUrl(
      { dropPrefix: sidebarQueryParamPrefix },
      { replace: true, state: fullNavigationState },
    );
  }, [writeShellUrl, isMobile]);

  /** Drop one of the open sidebar's params, leaving the sidebar itself up. */
  const removeSidebarParam = useCallback(
    (paramToRemove: string) => {
      writeShellUrl(
        { params: { [`${sidebarQueryParamPrefix}${paramToRemove}`]: null } },
        { replace: true, state: fullNavigationState },
      );
    },
    [writeShellUrl],
  );

  return {
    openSidebar,
    closeSidebar,
    removeSidebarParam,
    isOpen,
  };
};
